import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import BrandMark from "../components/BrandMark";
import {
  ArrowRight,
  CalendarCheck,
  Library,
  PoundSterling,
  Package,
  Bell,
  FileText,
  ShieldCheck,
  Users,
  Smartphone,
  Check,
  Sparkles,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1465847899084-d164df4dedc6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

const features = [
  {
    icon: CalendarCheck,
    title: "Attendance & Deps",
    desc:
      "Track availability, invite deps, and nudge non-responders with one tap. Every rehearsal and concert, sorted.",
  },
  {
    icon: Library,
    title: "Sheet Music Library",
    desc:
      "A searchable digital catalogue of every piece in your library. No more missing parts, no more duplicated orders.",
  },
  {
    icon: PoundSterling,
    title: "Band Finances",
    desc:
      "Subs, gig fees, dep payments — all categorised, with budget variance, P&L, and year-over-year reports.",
  },
  {
    icon: Package,
    title: "Inventory",
    desc:
      "Know exactly who has which instrument, uniform or piece of kit. Full loan history, condition tracking, return reminders.",
  },
  {
    icon: FileText,
    title: "Document Hub",
    desc:
      "Plug in your existing Google Drive folders. Members see the files they&rsquo;re meant to see — committee content stays committee-only.",
  },
  {
    icon: Bell,
    title: "Smart Reminders",
    desc:
      "Email + push notifications for upcoming events, RSVP nudges and committee alerts. Add your own message on top.",
  },
  {
    icon: Users,
    title: "Multi-Band Support",
    desc:
      "Play with two bands? One account. Your unified schedule spans every ensemble you&rsquo;re part of.",
  },
  {
    icon: Smartphone,
    title: "Installs as an app",
    desc:
      "One-tap home-screen install on iPhone and Android. Offline-friendly, push-enabled, no app store required.",
  },
];

const Check2 = () => <Check className="w-4 h-4 text-[#E6B800] flex-shrink-0 mt-0.5" />;

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-background/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <BrandMark size="md" />
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-login">
              Log in
            </Link>
            <Link to="/login?signup=1">
              <Button
                className="bg-[#E6B800] text-[#0A0908] hover:bg-[#CCA300] font-bold rounded-md h-9 sm:h-10 px-3 sm:px-5"
                data-testid="nav-signup-cta"
              >
                Start free trial
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative hero-spotlight">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-28 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          <div className="space-y-6 max-w-xl">
            <div className="overline inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Built for bands, choirs &amp; ensembles
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Run your group in <span className="italic" style={{ color: "#E6B800" }}>perfect harmony.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              From rehearsals and subs to inventory and sheet music — Vivo handles the admin so you can focus on the music. Brass, wind, orchestral, choral, jazz, folk — whatever you play, we&rsquo;ve got the paperwork.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/login?signup=1">
                <Button
                  size="lg"
                  className="bg-[#E6B800] text-[#0A0908] hover:bg-[#CCA300] font-bold rounded-md h-12 px-6 w-full sm:w-auto"
                  data-testid="hero-primary-cta"
                >
                  Start 30-day free trial
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-md h-12 px-6 border-white/10 text-foreground hover:bg-white/5 w-full sm:w-auto"
                  data-testid="hero-secondary-cta"
                >
                  Log in
                </Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">No credit card required · Cancel any time · £5/mo or £50/yr after trial</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-8 bg-[#E6B800]/10 blur-3xl rounded-full" />
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] aspect-[5/4]"
              style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
                <div className="bg-card/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-xs">
                  <div className="overline mb-0.5">Next rehearsal</div>
                  <div className="font-medium">Thursday · 19:30 · Parish Hall</div>
                </div>
                <div className="bg-card/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-xs hidden sm:block">
                  <div className="overline mb-0.5">Attending</div>
                  <div className="font-medium">28 of 32</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <section className="border-y border-white/5 bg-[#0F0D0B]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            Trusted by committees and music directors across the UK.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 opacity-50">
            {["Brass", "Wind Band", "Orchestra", "Choir", "Jazz Ensemble", "Youth Group"].map((n) => (
              <span key={n} className="text-sm uppercase tracking-[0.2em] font-medium">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="overline mb-3">Everything your committee needs</div>
          <h2 className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-4">
            One tool for every bit of band admin.
          </h2>
          <p className="text-muted-foreground text-lg">
            Built by musicians for musicians. Every feature exists because a committee somewhere needed it — whether that&rsquo;s a brass band, a choir, a jazz ensemble or a community orchestra.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-white/5 bg-[#171513] p-6 hover:border-[#E6B800]/20 transition-colors"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                style={{ background: "rgba(230, 184, 0, 0.1)", border: "1px solid rgba(230, 184, 0, 0.25)" }}
              >
                <f.icon className="w-5 h-5 text-[#E6B800]" />
              </div>
              <h3 className="font-heading text-xl mb-2 text-foreground">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow section */}
      <section className="bg-[#0F0D0B] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3">How it works</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight mb-6">
              Three steps to a well-run group.
            </h2>
            <ol className="space-y-5">
              {[
                { t: "Sign up and create your group", d: "One minute. Name it, pick your sections, invite your members with a join code." },
                { t: "Add events, music and members", d: "Or import everything in one go from a CSV. Drive folders plug in via a single paste." },
                { t: "Sit back and run the group", d: "Members RSVP, deps get invited automatically, reminders send themselves, finances reconcile themselves." },
              ].map((s, i) => (
                <li key={s.t} className="flex gap-4">
                  <div
                    className="w-9 h-9 flex-shrink-0 rounded-md flex items-center justify-center font-heading text-xl"
                    style={{ background: "rgba(230, 184, 0, 0.15)", color: "#E6B800", border: "1px solid rgba(230, 184, 0, 0.3)" }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-medium text-base text-foreground">{s.t}</div>
                    <p className="text-sm text-muted-foreground mt-0.5">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border border-white/10 bg-card p-6 sm:p-8 space-y-4 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
              <div className="overline ml-auto">This Thursday</div>
            </div>
            <div className="h-px bg-white/5" />
            <div>
              <div className="font-heading text-xl mb-1">Rehearsal — Parish Hall</div>
              <p className="text-sm text-muted-foreground">Thursday 19:30 · arrival 19:00</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ["28", "Attending", "#3E7B5C"],
                ["2", "Maybe", "#F9A825"],
                ["1", "Not attending", "#C95233"],
                ["1", "No response", "#777"],
              ].map(([n, l, c]) => (
                <div key={l} className="rounded-md px-3 py-2 border border-white/5 bg-[#0A0908]">
                  <div className="font-heading text-lg leading-none" style={{ color: c }}>{n}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <div className="h-px bg-white/5" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Programme</span>
              <span>The Floral Dance, Hootenanny, Slaidburn</span>
            </div>
            <Button className="w-full bg-[#E6B800] text-[#0A0908] hover:bg-[#CCA300] font-bold rounded-md">
              <Bell className="w-4 h-4 mr-2" /> Send reminder
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <div className="overline mb-3">Pricing</div>
        <h2 className="font-heading text-4xl sm:text-5xl mb-3 tracking-tight">Simple pricing. No hidden fees.</h2>
        <p className="text-muted-foreground mb-12">Everything your group needs to run smoothly — for less than the cost of one music stand.</p>

        <div className="grid sm:grid-cols-2 gap-4 text-left">
          <div className="rounded-xl border border-white/5 bg-[#171513] p-6 sm:p-8">
            <div className="overline mb-2">Monthly</div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-heading text-5xl">£5</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="space-y-2 text-sm mb-6">
              <li className="flex gap-2"><Check2 /> Unlimited members, events and music</li>
              <li className="flex gap-2"><Check2 /> All admin modules (finance, inventory, documents)</li>
              <li className="flex gap-2"><Check2 /> Committee email alias</li>
              <li className="flex gap-2"><Check2 /> Cancel any time</li>
            </ul>
            <Link to="/login?signup=1">
              <Button variant="outline" className="w-full h-11 rounded-md border-white/10 hover:bg-white/5">
                Start free trial
              </Button>
            </Link>
          </div>

          <div className="rounded-xl border-2 p-6 sm:p-8 relative" style={{ borderColor: "#E6B800", background: "linear-gradient(to bottom right, #1A1612, #171513)" }}>
            <div className="absolute -top-3 right-4 bg-[#E6B800] text-[#0A0908] text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded">
              Save £10
            </div>
            <div className="overline mb-2">Annual</div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-heading text-5xl">£50</span>
              <span className="text-muted-foreground">/year</span>
            </div>
            <ul className="space-y-2 text-sm mb-6">
              <li className="flex gap-2"><Check2 /> Everything in Monthly</li>
              <li className="flex gap-2"><Check2 /> Two months free vs monthly</li>
              <li className="flex gap-2"><Check2 /> Priority email support</li>
              <li className="flex gap-2"><Check2 /> Early access to new features</li>
            </ul>
            <Link to="/login?signup=1">
              <Button className="w-full h-11 bg-[#E6B800] text-[#0A0908] hover:bg-[#CCA300] font-bold rounded-md" data-testid="pricing-annual-cta">
                Start 30-day free trial
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-6">30-day free trial · No credit card required · Billed via Stripe.</p>
      </section>

      {/* Final CTA band */}
      <section className="bg-[#0F0D0B] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-4 text-[#E6B800]" />
          <h2 className="font-heading text-3xl sm:text-4xl mb-3 tracking-tight">Your data. Your group. Your rules.</h2>
          <p className="text-muted-foreground mb-6">
            GDPR-compliant by design. Export or delete your data any time. Multi-tenant isolation means no one outside your group ever sees a thing.
          </p>
          <Link to="/login?signup=1">
            <Button size="lg" className="bg-[#E6B800] text-[#0A0908] hover:bg-[#CCA300] font-bold rounded-md h-12 px-8" data-testid="footer-cta">
              Start your 30-day free trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandMark size="sm" />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-foreground transition-colors" data-testid="landing-footer-support">Support</Link>
            <a href="mailto:james@support.vivomusicianmanagement.co.uk" className="hover:text-foreground transition-colors">Contact</a>
          </div>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Vivo · James Fiddeman</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
